/**
 * Client HTTP du back-office, typé sur `@genviz/shared`.
 *
 * Trois responsabilités, et rien d'autre :
 *  1. transporter (URL, en-têtes, sérialisation, annulation) ;
 *  2. traduire toute réponse non-2xx en `ErreurApi` portant un CODE du catalogue partagé —
 *     l'API ne renvoie jamais de message lisible, la traduction se fait à l'affichage ;
 *  3. rejouer UNE fois la requête après rafraîchissement du jeton, pour qu'une session qui
 *     expire pendant qu'un opérateur lit un diagnostic ne le renvoie pas à l'écran de connexion.
 *
 * Le mode maquette est branché ici : quand il est actif, aucune requête réseau n'est émise,
 * le jeu de données factice répond à la place. C'est ce qui permet de développer et de
 * démontrer l'interface avant que le backend n'existe.
 *
 * Aucun JWT n'est lu ni décodé : les jetons sont OPAQUES (docs/06-revue-critique.md). Le client
 * ne sait rien de leur contenu, seulement les transmettre et les faire renouveler.
 */

import { EnteteHttp } from '@genviz/shared/common';
import {
  CodeErreur,
  estReponseErreurApi,
  type ErreurChamp,
  type ReponseErreurApi,
} from '@genviz/shared/errors';
import type {
  DesassociationForceeRequete,
  DesassociationForceeReponse,
  DiagnosticAppareilReponse,
  ImportStockRequete,
  ImportStockReponse,
  ListeJournalAuditRequete,
  ListeJournalAuditReponse,
  OperateurInterne,
  RechercheStockRequete,
  RechercheStockReponse,
} from '@genviz/shared/dto';

import { URL_API, modeMaquette } from '../config';
import { identifiantUnique } from '../lib/divers';
import { CHEMINS } from './endpoints';
import type {
  ChangementStatutExploitationRequete,
  ChangementStatutExploitationReponse,
  ChangementStatutStockRequete,
  ChangementStatutStockReponse,
  ChangementStatutUtilisateurRequete,
  ChangementStatutUtilisateurReponse,
  ConnexionStaffRequete,
  ConnexionStaffReponse,
  DeconnexionStaffRequete,
  DetailExploitationReponse,
  FiltreHistoriqueRequete,
  FiltreHistoriqueTramesRequete,
  LigneImpulsionHistorique,
  EtalonnageReponse,
  EtalonnageRequete,
  EtalonnageResultat,
  LigneMesureHistorique,
  LigneTrameHistorique,
  ListeExploitationsRequete,
  ListeExploitationsReponse,
  MesuresRecentesRequete,
  MesuresRecentesReponse,
  PageHistorique,
  RafraichissementStaffRequete,
  RafraichissementStaffReponse,
  SupervisionCarteRequete,
  SupervisionCarteReponse,
  TableauDeBordReponse,
} from './types';

/* -------------------------------------------------------------------------- */
/* Erreur                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * Erreur d'API. Porte le code du catalogue partagé, jamais un message serveur : le texte
 * affiché est choisi par le back-office (`i18n/fr.ts`).
 */
export class ErreurApi extends Error {
  readonly code: CodeErreur;
  readonly http: number;
  readonly champs: ErreurChamp[];
  readonly traceId: string | undefined;
  readonly reessayerDansS: number | undefined;
  readonly contexte: Record<string, unknown> | undefined;

  constructor(reponse: ReponseErreurApi) {
    super(reponse.code);
    this.name = 'ErreurApi';
    this.code = reponse.code;
    this.http = reponse.http;
    this.champs = reponse.champs ?? [];
    this.traceId = reponse.trace_id;
    this.reessayerDansS = reponse.reessayer_dans_s;
    this.contexte = reponse.contexte;
  }

  /** Vrai si l'appelant doit refaire authentifier l'opérateur. */
  get estAuthentification(): boolean {
    return this.http === 401;
  }

  /** Vrai si l'opération a échoué faute de droits — inutile de réessayer. */
  get estInterdit(): boolean {
    return this.http === 403;
  }
}

/** Construit une `ErreurApi` locale (réseau, réponse illisible) sans passer par le serveur. */
function erreurLocale(code: CodeErreur, http: number): ErreurApi {
  return new ErreurApi({ code, http });
}

/* -------------------------------------------------------------------------- */
/* Pont de session                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Le client ne connaît pas le stockage des jetons : `auth/session.ts` l'y branche. Cette
 * indirection évite un cycle d'import (la session appelle le client pour rafraîchir, le client
 * demande son jeton à la session) et rend le client testable sans session.
 */
export interface PontSession {
  /** Jeton d'accès courant, `null` si l'opérateur n'est pas connecté. */
  jetonAcces: () => string | null;
  /** Tente un rafraîchissement ; renvoie vrai si un nouveau jeton est disponible. */
  rafraichir: () => Promise<boolean>;
  /** Appelé quand la session est définitivement perdue (rafraîchissement refusé). */
  sessionPerdue: () => void;
}

let pont: PontSession = {
  jetonAcces: () => null,
  rafraichir: async () => false,
  sessionPerdue: () => undefined,
};

export function brancherSession(nouveauPont: PontSession): void {
  pont = nouveauPont;
}

/* -------------------------------------------------------------------------- */
/* Transport                                                                   */
/* -------------------------------------------------------------------------- */

export type Methode = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';

export type ParametresRequete = Record<
  string,
  string | number | boolean | readonly string[] | undefined | null
>;

export interface OptionsRequete {
  query?: ParametresRequete;
  corps?: unknown;
  /** Ajoute une clé d'idempotence : obligatoire sur les créations et les commandes. */
  idempotent?: boolean;
  /** Requête émise sans jeton (connexion, rafraîchissement). */
  sansAuthentification?: boolean;
  signal?: AbortSignal;
}

function construireUrl(chemin: string, query: ParametresRequete | undefined): string {
  const base = `${URL_API}${chemin}`;
  if (!query) return base;

  const parametres = new URLSearchParams();
  for (const [cle, valeur] of Object.entries(query)) {
    if (valeur === undefined || valeur === null || valeur === '') continue;
    if (Array.isArray(valeur)) {
      for (const v of valeur) parametres.append(cle, String(v));
    } else {
      parametres.set(cle, String(valeur));
    }
  }
  const chaine = parametres.toString();
  return chaine === '' ? base : `${base}?${chaine}`;
}

/** Transforme une réponse non-2xx en `ErreurApi`, en acceptant un corps absent ou illisible. */
async function versErreur(reponse: Response): Promise<ErreurApi> {
  let charge: unknown = null;
  try {
    charge = await reponse.json();
  } catch {
    charge = null;
  }

  if (estReponseErreurApi(charge)) return new ErreurApi(charge);

  // Le serveur n'a pas parlé le contrat (proxy, passerelle, page d'erreur nginx) : on retombe
  // sur un code cohérent avec le statut plutôt que d'inventer un message.
  const parStatut: Record<number, CodeErreur> = {
    400: CodeErreur.ERREUR_VALIDATION,
    401: CodeErreur.NON_AUTHENTIFIE,
    403: CodeErreur.ROLE_NON_AUTORISE,
    404: CodeErreur.RESSOURCE_INTROUVABLE,
    409: CodeErreur.PRECONDITION_ECHOUEE,
    412: CodeErreur.PRECONDITION_ECHOUEE,
    422: CodeErreur.ERREUR_VALIDATION,
    429: CodeErreur.TROP_DE_REQUETES,
    503: CodeErreur.SERVICE_INDISPONIBLE,
  };
  return erreurLocale(parStatut[reponse.status] ?? CodeErreur.ERREUR_INTERNE, reponse.status);
}

async function envoyer<T>(
  methode: Methode,
  chemin: string,
  options: OptionsRequete,
  dejaRejouee: boolean,
): Promise<T> {
  const entetes: Record<string, string> = {
    Accept: 'application/json',
    [EnteteHttp.TRACE]: identifiantUnique(),
  };

  if (options.corps !== undefined) entetes['Content-Type'] = 'application/json';
  if (options.idempotent) entetes[EnteteHttp.IDEMPOTENCE] = identifiantUnique();

  if (!options.sansAuthentification) {
    const jeton = pont.jetonAcces();
    if (jeton) entetes.Authorization = `Bearer ${jeton}`;
  }

  let reponse: Response;
  try {
    reponse = await fetch(construireUrl(chemin, options.query), {
      method: methode,
      headers: entetes,
      body: options.corps === undefined ? undefined : JSON.stringify(options.corps),
      signal: options.signal ?? null,
      credentials: 'omit',
      cache: 'no-store',
    });
  } catch (cause) {
    // Requête annulée par TanStack Query (changement d'écran) : on propage tel quel.
    if (cause instanceof DOMException && cause.name === 'AbortError') throw cause;
    throw erreurLocale(CodeErreur.SERVICE_INDISPONIBLE, 0);
  }

  if (reponse.status === 401 && !options.sansAuthentification && !dejaRejouee) {
    const rafraichi = await pont.rafraichir();
    if (rafraichi) return envoyer<T>(methode, chemin, options, true);
    pont.sessionPerdue();
  }

  if (!reponse.ok) throw await versErreur(reponse);

  if (reponse.status === 204 || reponse.headers.get('Content-Length') === '0') {
    return undefined as T;
  }

  try {
    return (await reponse.json()) as T;
  } catch {
    throw erreurLocale(CodeErreur.ERREUR_INTERNE, reponse.status);
  }
}

/** Point d'entrée unique du transport : aiguille vers le réseau ou vers la maquette. */
async function requete<T>(
  methode: Methode,
  chemin: string,
  options: OptionsRequete = {},
): Promise<T> {
  if (modeMaquette()) {
    // Chargé à la demande : le jeu de données factice n'entre pas dans le bundle de production.
    const { executerMaquette } = await import('./mock/serveur');
    return executerMaquette<T>(methode, chemin, options);
  }
  return envoyer<T>(methode, chemin, options, false);
}

/* -------------------------------------------------------------------------- */
/* Surface typée — un point d'appel par endpoint                               */
/* -------------------------------------------------------------------------- */

export const api = {
  /* --- Session ---------------------------------------------------------- */
  auth: {
    connexion(corps: ConnexionStaffRequete, signal?: AbortSignal): Promise<ConnexionStaffReponse> {
      return requete('POST', CHEMINS.connexion, {
        corps,
        sansAuthentification: true,
        ...(signal ? { signal } : {}),
      });
    },

    rafraichir(corps: RafraichissementStaffRequete): Promise<RafraichissementStaffReponse> {
      return requete('POST', CHEMINS.rafraichissement, { corps, sansAuthentification: true });
    },

    deconnexion(corps: DeconnexionStaffRequete): Promise<void> {
      return requete('POST', CHEMINS.deconnexion, { corps });
    },

    moi(signal?: AbortSignal): Promise<OperateurInterne> {
      return requete('GET', CHEMINS.moi, { ...(signal ? { signal } : {}) });
    },
  },

  /* --- Tableau de bord --------------------------------------------------- */
  tableauDeBord: {
    charger(signal?: AbortSignal): Promise<TableauDeBordReponse> {
      return requete('GET', CHEMINS.tableauDeBord, { ...(signal ? { signal } : {}) });
    },
  },

  /* --- Stock ------------------------------------------------------------- */
  stock: {
    rechercher(
      parametres: RechercheStockRequete,
      signal?: AbortSignal,
    ): Promise<RechercheStockReponse> {
      return requete('GET', CHEMINS.stock, {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    importer(corps: ImportStockRequete): Promise<ImportStockReponse> {
      return requete('POST', CHEMINS.importStock, { corps, idempotent: true });
    },

    changerStatut(
      numeroSerie: string,
      corps: ChangementStatutStockRequete,
    ): Promise<ChangementStatutStockReponse> {
      return requete('POST', CHEMINS.statutStock(numeroSerie), { corps, idempotent: true });
    },
  },

  /* --- Appareils --------------------------------------------------------- */
  appareils: {
    diagnostic(
      numeroSerie: string,
      parametres: { depuis?: string; limite_trames?: number } = {},
      signal?: AbortSignal,
    ): Promise<DiagnosticAppareilReponse> {
      return requete('GET', CHEMINS.diagnostic(numeroSerie), {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    historiqueMesures(
      numeroSerie: string,
      parametres: FiltreHistoriqueRequete,
      signal?: AbortSignal,
    ): Promise<PageHistorique<LigneMesureHistorique>> {
      return requete('GET', CHEMINS.historiqueMesures(numeroSerie), {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    historiqueTrames(
      numeroSerie: string,
      parametres: FiltreHistoriqueTramesRequete,
      signal?: AbortSignal,
    ): Promise<PageHistorique<LigneTrameHistorique>> {
      return requete('GET', CHEMINS.historiqueTrames(numeroSerie), {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    historiqueImpulsions(
      numeroSerie: string,
      parametres: FiltreHistoriqueRequete,
      signal?: AbortSignal,
    ): Promise<PageHistorique<LigneImpulsionHistorique>> {
      return requete('GET', CHEMINS.historiqueImpulsions(numeroSerie), {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    mesures(
      numeroSerie: string,
      parametres: MesuresRecentesRequete = {},
      signal?: AbortSignal,
    ): Promise<MesuresRecentesReponse> {
      return requete('GET', CHEMINS.mesures(numeroSerie), {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    etalonnage(numeroSerie: string, signal?: AbortSignal): Promise<EtalonnageReponse> {
      return requete('GET', CHEMINS.etalonnage(numeroSerie), { ...(signal ? { signal } : {}) });
    },

    etalonner(numeroSerie: string, corps: EtalonnageRequete): Promise<EtalonnageResultat> {
      return requete('POST', CHEMINS.etalonnage(numeroSerie), { corps, idempotent: true });
    },

    desassocier(
      numeroSerie: string,
      corps: DesassociationForceeRequete,
    ): Promise<DesassociationForceeReponse> {
      return requete('POST', CHEMINS.desassociation(numeroSerie), { corps, idempotent: true });
    },
  },

  /* --- Exploitations ----------------------------------------------------- */
  exploitations: {
    lister(
      parametres: ListeExploitationsRequete,
      signal?: AbortSignal,
    ): Promise<ListeExploitationsReponse> {
      return requete('GET', CHEMINS.exploitations, {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },

    detail(id: string, signal?: AbortSignal): Promise<DetailExploitationReponse> {
      return requete('GET', CHEMINS.exploitation(id), { ...(signal ? { signal } : {}) });
    },

    changerStatut(
      id: string,
      corps: ChangementStatutExploitationRequete,
    ): Promise<ChangementStatutExploitationReponse> {
      return requete('POST', CHEMINS.statutExploitation(id), { corps, idempotent: true });
    },

    changerStatutUtilisateur(
      id: string,
      corps: ChangementStatutUtilisateurRequete,
    ): Promise<ChangementStatutUtilisateurReponse> {
      return requete('POST', CHEMINS.statutUtilisateur(id), { corps, idempotent: true });
    },
  },

  /* --- Supervision ------------------------------------------------------- */
  supervision: {
    carte(
      parametres: SupervisionCarteRequete = {},
      signal?: AbortSignal,
    ): Promise<SupervisionCarteReponse> {
      const { etats, ...reste } = parametres;
      return requete('GET', CHEMINS.carteSupervision, {
        query: { ...reste, ...(etats && etats.length > 0 ? { etats } : {}) },
        ...(signal ? { signal } : {}),
      });
    },
  },

  /* --- Journal d'audit --------------------------------------------------- */
  audit: {
    lister(
      parametres: ListeJournalAuditRequete,
      signal?: AbortSignal,
    ): Promise<ListeJournalAuditReponse> {
      return requete('GET', CHEMINS.audit, {
        query: { ...parametres },
        ...(signal ? { signal } : {}),
      });
    },
  },

  /* --- Exploitation commerciale ------------------------------------------ */
  firmware: {
    descriptionFlash(signal?: AbortSignal): Promise<import('./types').DescriptionFlash> {
      return requete('GET', CHEMINS.flashDescription, { ...(signal ? { signal } : {}) });
    },
    /** Contient le secret du lot : POST, tracé au journal d'audit côté serveur. */
    imageFlash(): Promise<import('./types').ImageFlash> {
      return requete('POST', CHEMINS.flashImage, {});
    },
  },

  commercial: {
    clients(filtre: ParametresRequete = {}, signal?: AbortSignal): Promise<import('./types').ListeClientsReponse> {
      return requete('GET', CHEMINS.clients, { query: filtre, ...(signal ? { signal } : {}) });
    },
    consommation(filtre: ParametresRequete = {}, signal?: AbortSignal): Promise<import('./types').ConsommationReponse> {
      return requete('GET', CHEMINS.consommation, { query: filtre, ...(signal ? { signal } : {}) });
    },
    abonnements(filtre: ParametresRequete = {}, signal?: AbortSignal): Promise<import('./types').ListeAbonnementsReponse> {
      return requete('GET', CHEMINS.abonnements, { query: filtre, ...(signal ? { signal } : {}) });
    },
    souscrire(corps: import('./types').SouscriptionRequete): Promise<import('./types').Abonnement> {
      return requete('POST', CHEMINS.abonnements, { corps });
    },
    changerStatutAbonnement(
      id: string,
      corps: import('./types').ChangementStatutAbonnementRequete,
    ): Promise<{ id: string; statut: string; executee_at: string }> {
      return requete('POST', CHEMINS.statutAbonnement(id), { corps });
    },
    tickets(filtre: ParametresRequete = {}, signal?: AbortSignal): Promise<import('./types').ListeTicketsReponse> {
      return requete('GET', CHEMINS.tickets, { query: filtre, ...(signal ? { signal } : {}) });
    },
    ticket(id: string, signal?: AbortSignal): Promise<import('./types').DetailTicketReponse> {
      return requete('GET', CHEMINS.ticket(id), { ...(signal ? { signal } : {}) });
    },
    repondreTicket(id: string, corps: import('./types').ReponseTicketRequete): Promise<{ id: string }> {
      return requete('POST', CHEMINS.messagesTicket(id), { corps });
    },
    changerStatutTicket(
      id: string,
      corps: import('./types').ChangementStatutTicketRequete,
    ): Promise<{ id: string; statut: string }> {
      return requete('POST', CHEMINS.statutTicket(id), { corps });
    },
  },
} as const;


export type ClientApi = typeof api;
